import { Component, Input, OnDestroy } from '@angular/core';
import { ClipboardService } from 'ngx-clipboard';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { ClusivityListItemsComponent } from '../clusivity-list-items.component';
import { Common } from '../../../_common/common.helper';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, takeUntil, tap } from 'rxjs/operators';
import { UploadFileType } from '@/app/shared/_models/models';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { FileStatus, ValidationResult } from '../../../_components/upload-file/upload-file.component';

@Component({
  selector: 'app-clusivity-list-multi-text-input',
  templateUrl: './clusivity-list-multi-text-input.component.html',
  styleUrls: ['./clusivity-list-multi-text-input.component.less'],
  providers: [{
    provide: ClusivityListItemsComponent,
    useExisting: ClusivityListMultiTextInputComponent
  }]
})
export class ClusivityListMultiTextInputComponent<TItem> extends ClusivityListItemsComponent<TItem> implements OnDestroy {
  @Input() placeHolder = '';
  @Input() maxTagCount = 2;
  @Input() selectElementId = '';
  @Input() enableCsvUpload = false;
  @Input() itemsTokenSeparators: string[] = [',', '\n'];

  public isCopiedItems = false;
  public selectIcons: { [p: string]: boolean } = {};
  public uploadedFile: NzUploadFile[] = [];
  public fileTypes: UploadFileType[] = ['text/csv' as UploadFileType];

  private parsedCsvData: string[][] | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private clipboardService: ClipboardService,
    private notification: NzNotificationService,
    private common: Common
  ) {
    super();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public clipboardCopy(text: string, inlineNotification = false): void {
    this.clipboardService.copy(text);
    if (inlineNotification) {
      this.isCopiedItems = true;
      setTimeout(() => {
        this.isCopiedItems = false;
      }, 1500);
    } else {
      this.notification.success('Success', 'Copied to clipboard.');
    }
  }

  public csvParsingValidation = (file: NzUploadFile): Observable<ValidationResult> => {
    const actualFile = file.originFileObj || file;
    if (!(actualFile instanceof File)) {
      return of({ isValid: false, errorMessage: 'Invalid file object.' });
    }
    return this.common.parseCSV<string[]>(actualFile, false, false, [','])
      .pipe(
        takeUntil(this.destroy$),
        tap(data => {
          this.parsedCsvData = data;
        }),
        map(_data => ({ isValid: true } as ValidationResult)),
        catchError(_error =>
          of({ isValid: false, errorMessage: 'Error parsing CSV. Please check format.' } as ValidationResult)
        )
      );
  };

  public onFileUploadFinished(event: { file: NzUploadFile }): void {
    const { file } = event;
    if (file.status === FileStatus.DONE) {
      this.processParsedCsvData();
    } else if (file.status === FileStatus.ERROR) {
      this.handleUploadError();
    }
  }

  public onFileRemoved(_event: NzUploadFile): void {
    this.resetUploadState();
  }

  private processParsedCsvData(): void {
    if (!this.validateCsvData()) {
      return;
    }

    const { validItems: newItemsFromFile, emptyItemsCount } =
      this.extractItemsFromParsedData(this.parsedCsvData);

    if (this.handleEmptyValidItemsCase(newItemsFromFile, emptyItemsCount)) {
      return;
    }

    const itemsActuallyAdded = this.updateFormWithValidItems(newItemsFromFile);
    this.showNotificationMessage(newItemsFromFile, itemsActuallyAdded, emptyItemsCount);
    this.resetUploadState();
  }

  private validateCsvData(): boolean {
    if (!this.parsedCsvData || this.parsedCsvData.length === 0) {
      this.notification.warning('Warning', 'CSV processed, but no valid data was extracted.');
      this.resetUploadState();
      return false;
    }
    return true;
  }

  private handleEmptyValidItemsCase(validItems: string[], emptyItemsCount: number): boolean {
    if (validItems.length === 0) {
      if (emptyItemsCount > 0) {
        this.notification.warning(
          'Warning',
          `CSV contained only empty values or whitespace (${emptyItemsCount} total).`
        );
      } else {
        this.notification.warning('Warning', 'No valid items found in the uploaded file.');
      }
      this.resetUploadState();
      return true;
    }
    return false;
  }

  private updateFormWithValidItems(newItems: string[]): number {
    const countBeforeUpdate = (this.itemsFormControl.value || []).length;
    this.updateFormControlWithItems(newItems);
    const countAfterUpdate = (this.itemsFormControl.value || []).length;
    return countAfterUpdate - countBeforeUpdate;
  }

  private showNotificationMessage(validItems: string[], itemsAdded: number, emptyItemsCount: number): void {
    let detailedMsg = '';

    if (itemsAdded > 0) {
      detailedMsg = `Added ${itemsAdded} new unique item(s) from file.`;
      if (emptyItemsCount > 0) {
        detailedMsg += ` ${emptyItemsCount} empty/whitespace values were filtered out.`;
      }
      this.notification.success('Success', detailedMsg);
    } else if (validItems.length > 0 && itemsAdded === 0) {
      detailedMsg = `All ${validItems.length} valid item(s) from the file were already in the list.`;
      if (emptyItemsCount > 0) {
        detailedMsg += ` ${emptyItemsCount} empty values were filtered out.`;
      }
      this.notification.info('Information', detailedMsg);
    }
  }

  private extractItemsFromParsedData(parsedData: string[][]): { validItems: string[]; emptyItemsCount: number } {
    const totalItems = parsedData.length;
    const validItems = parsedData
      .map(row => {
        if (row && row.length > 0) {
          return String(row[0]).trim();
        }
        return '';
      })
      .filter(item => !!item && item !== 'null');
    const emptyItemsCount = totalItems - validItems.length;
    return { validItems, emptyItemsCount };
  }

  private updateFormControlWithItems(newItems: string[]): void {
    const currentItems = this.itemsFormControl.value || [];
    const currentStrings = currentItems.map(item => String(item));
    const mergedItems = [...new Set([...currentStrings, ...newItems])];
    this.itemsFormControl.setValue(mergedItems as TItem[]);
  }

  private handleUploadError(): void {
    this.resetUploadState();
  }

  private resetUploadState(): void {
    this.uploadedFile = [];
    this.parsedCsvData = null;
  }
}
